import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";

const title = "Student Health Team | Swedish International School Estepona";
const description =
  "Our student health team is led by the principal and includes a special education needs coordinator and a psychologist, supporting pupil health and wellbeing.";

export const Route = createFileRoute("/en/student-health-team")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/student-health-team") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/student-health-team"),
  }),
  component: StudentHealthTeam,
});

function StudentHealthTeam() {
  return (
    <>
      <PageHero
        eyebrow="Safety and health"
        title="Student health team"
        intro="The school has a student health team led by the principal, consisting of a special education needs coordinator and a psychologist."
      />

      <Prose>
        <p>
          The student health team is an important part of the school's overall structure and is made
          up of skilled professionals who work together to promote pupils' health and wellbeing. The
          team provides a varied, holistic approach to meeting different needs and challenges.
        </p>

        <h2>School psychologist</h2>
        <p>
          The school psychologist provides expertise in the psychological aspects of pupils'
          development and learning. Through psychological assessments and interventions, the school
          psychologist supports pupils who may have specific needs in this area. The school
          psychologist also works with teachers and parents to create a favourable environment for
          pupils' mental health. Our school psychologist is an external consultant brought in as
          needed, allowing the school to draw on their expertise when required.
        </p>

        <h2>Led by the principal</h2>
        <p>
          The principal plays a central role as the leader of the student health team. By providing
          guidelines, resources and support, the principal enables effective work and collaboration
          within the team. The principal is also responsible for integrating the student health
          perspective into the school's overall strategies and decisions.
        </p>

        <p>
          This forms our varied and coordinated student health team, which extends beyond teaching to
          support pupils at different levels and ensure a safe and healthy school environment. By
          working together, the team can build a complete picture of pupils' needs and contribute to
          an inclusive and welcoming school environment.
        </p>

        <h2>Special needs teacher</h2>
        <p>
          The special needs teacher works closely with class teachers on assessment, classroom
          adaptations and follow-up for pupils who need more support in a subject. That may mean extra
          practice in reading and writing, structure and study technique, or finding the right level
          for a pupil who needs greater challenge.
        </p>

        <h2>How to get in touch</h2>
        <p>
          First contact always goes through the class teacher or the principal, who brings the matter
          to the team. You can also email{" "}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">info@sweintschool.com</a>{" "}
          and the school will suggest a time. Pupils themselves can turn to any adult at the school
          when they need to talk to someone.
        </p>

        <h2>Confidentiality and cooperation with home</h2>
        <p>
          Matters raised in student health work are handled with respect for the pupil's and the
          family's privacy, and information is shared only with those who need it in order to help.
          Guardians are involved in measures concerning their own child. If a pupil needs care or
          assessment outside school we help point the way, but the school does not provide healthcare.
        </p>

        <h2>Preventive work</h2>
        <p>
          Much of student health is about what happens before problems arise: safety during breaks,
          group-building work in classes, clear routines at the start of school and conversations
          about how we treat each other. Read more about how we welcome new families on our{" "}
          <a className="text-primary link-quiet" href="/en/community">community page</a>.
        </p>
      </Prose>

      <FaqBlock items={faqFor("elevhalsa", "en")} locale={"en"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
